import { useMemo, useState } from 'react';
import type { DraftState } from '../types';
import { validateTrade, type TradeSpec } from '../lib/draftLogic';
import { TeamLogo } from './TeamLogo';
import { TEAMS_BY_ABBR } from '../constants/teams';

interface Props {
  state: DraftState;
  onClose: () => void;
  onExecute: (spec: TradeSpec) => Promise<void>;
}

export function TradeModal({ state, onClose, onExecute }: Props) {
  const eligibleManagers = state.managers.filter(m => m.teams.length > 0);
  const defaultA = eligibleManagers[0]?.id ?? state.managers[0].id;
  const defaultB = eligibleManagers.find(m => m.id !== defaultA)?.id ?? state.managers[1]?.id ?? defaultA;

  const [aId, setAId] = useState(defaultA);
  const [bId, setBId] = useState(defaultB);
  const [teamsFromA, setTeamsFromA] = useState<string[]>([]);
  const [teamsFromB, setTeamsFromB] = useState<string[]>([]);
  const [cashFromA, setCashFromA] = useState(0);
  const [cashFromB, setCashFromB] = useState(0);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const a = state.managers.find(m => m.id === aId)!;
  const b = state.managers.find(m => m.id === bId)!;

  const spec: TradeSpec = {
    managerAId: aId,
    managerBId: bId,
    teamsFromA, teamsFromB,
    cashFromA: cashFromA || undefined,
    cashFromB: cashFromB || undefined,
    note: note.trim() || undefined,
  };
  const err = useMemo(() => validateTrade(state, spec), [state, aId, bId, teamsFromA, teamsFromB, cashFromA, cashFromB]);

  const submit = async () => {
    if (err) return;
    setSubmitting(true);
    try {
      await onExecute(spec);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const reset = (newA: string, newB: string) => {
    setAId(newA); setBId(newB);
    setTeamsFromA([]); setTeamsFromB([]);
    setCashFromA(0); setCashFromB(0);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]">
      <div className="panel p-6 w-full max-w-3xl border-2 border-teal-500 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-2xl font-extrabold text-teal-400">🔄 Execute Trade</h3>
            <p className="text-xs text-slate-400">Post-draft team & cash swap between two managers.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <ManagerSide
            label="Manager A gives ➡"
            state={state}
            myId={aId}
            otherId={bId}
            onChangeMe={id => reset(id, id === bId ? aId : bId)}
            selectedTeams={teamsFromA}
            onToggleTeam={abbr => setTeamsFromA(t => t.includes(abbr) ? t.filter(x => x !== abbr) : [...t, abbr])}
            cash={cashFromA}
            onCash={setCashFromA}
          />
          <ManagerSide
            label="Manager B gives ⬅"
            state={state}
            myId={bId}
            otherId={aId}
            onChangeMe={id => reset(id === aId ? bId : aId, id)}
            selectedTeams={teamsFromB}
            onToggleTeam={abbr => setTeamsFromB(t => t.includes(abbr) ? t.filter(x => x !== abbr) : [...t, abbr])}
            cash={cashFromB}
            onCash={setCashFromB}
          />
        </div>

        {/* Preview */}
        <div className="mt-4 bg-slate-900/60 rounded-lg p-3 border border-slate-700">
          <div className="text-xs uppercase text-slate-400 mb-2">Trade Preview</div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <strong className="text-teal-300">{a.emoji} {a.name}</strong>
            <span className="text-slate-400">sends</span>
            <TeamChips abbrs={teamsFromA} />
            {cashFromA > 0 && <span className="text-emerald-400 font-bold">+ ${cashFromA}</span>}
            <span className="text-slate-500">→</span>
            <strong className="text-teal-300">{b.emoji} {b.name}</strong>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm mt-2">
            <strong className="text-teal-300">{b.emoji} {b.name}</strong>
            <span className="text-slate-400">sends</span>
            <TeamChips abbrs={teamsFromB} />
            {cashFromB > 0 && <span className="text-emerald-400 font-bold">+ ${cashFromB}</span>}
            <span className="text-slate-500">→</span>
            <strong className="text-teal-300">{a.emoji} {a.name}</strong>
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase text-slate-400">Note (optional)</label>
          <input
            type="text"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="e.g. 'Week 4 trade'"
            className="w-full mt-1 p-2 bg-slate-900 border border-slate-700 rounded text-white text-sm"
          />
        </div>

        {err && (
          <div className="mt-3 p-2 bg-rose-900/40 border border-rose-500 rounded text-rose-300 text-sm">
            {err}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={!!err || submitting} className="btn-primary">
            {submitting ? 'Executing…' : '🤝 Execute Trade'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManagerSide({
  label, state, myId, otherId, onChangeMe, selectedTeams, onToggleTeam, cash, onCash,
}: {
  label: string;
  state: DraftState;
  myId: string;
  otherId: string;
  onChangeMe: (id: string) => void;
  selectedTeams: string[];
  onToggleTeam: (abbr: string) => void;
  cash: number;
  onCash: (n: number) => void;
}) {
  const m = state.managers.find(x => x.id === myId)!;
  return (
    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
      <div className="text-xs uppercase text-slate-400 mb-1">{label}</div>
      <select
        value={myId}
        onChange={e => onChangeMe(e.target.value)}
        className="w-full p-2 bg-slate-900 border border-slate-700 rounded text-white font-bold mb-2"
      >
        {state.managers.map(opt => (
          <option key={opt.id} value={opt.id} disabled={opt.id === otherId}>
            {opt.emoji} {opt.name} (${opt.budget}, {opt.teams.length} teams)
          </option>
        ))}
      </select>

      <div className="text-[10px] uppercase text-slate-500 mb-1">Teams (click to toggle)</div>
      {m.teams.length === 0 ? (
        <p className="text-xs text-slate-500 italic mb-2">No teams owned.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {m.teams.map(abbr => {
            const sel = selectedTeams.includes(abbr);
            return (
              <button
                key={abbr}
                onClick={() => onToggleTeam(abbr)}
                className={`flex items-center gap-1 px-2 py-1 rounded border text-xs font-bold transition ${
                  sel
                    ? 'bg-teal-500 text-slate-900 border-teal-400'
                    : 'bg-slate-900 text-slate-300 border-slate-700 hover:border-teal-400'
                }`}
              >
                <TeamLogo abbr={abbr} size={16} />
                {abbr}
              </button>
            );
          })}
        </div>
      )}

      <div className="text-[10px] uppercase text-slate-500 mb-1">Cash (optional, max ${m.budget})</div>
      <input
        type="number"
        min={0}
        max={m.budget}
        value={cash}
        onChange={e => onCash(Math.max(0, Math.min(m.budget, Math.floor(Number(e.target.value) || 0))))}
        className="w-full p-2 bg-slate-900 border border-slate-700 rounded text-white text-sm text-right font-bold"
      />
    </div>
  );
}

function TeamChips({ abbrs }: { abbrs: string[] }) {
  if (abbrs.length === 0) return <span className="text-slate-600 italic text-xs">(no teams)</span>;
  return (
    <>
      {abbrs.map(a => (
        <span key={a} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-800 rounded border border-slate-700 text-xs">
          <TeamLogo abbr={a} size={14} />
          {TEAMS_BY_ABBR[a]?.name ?? a}
        </span>
      ))}
    </>
  );
}
